import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PERMISSION_PERSONNEL_RECORD_REQUEST_APPROVAL,
  PERMISSION_PERSONNEL_RECORD_APPROVAL,
} from 'constants/permissions';
import {
  PROFILE_DRAFT,
  PROFILE_PENDING,
  PROFILE_DENIED,
  PROFILE_INACTIVE,
} from 'constants/profiles';
import CustomButton from 'styles/buttonStyled';
import { Modal, message, Button, Select, Spin } from 'antd';
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useHistory } from 'react-router-dom';
import { sendMail, getListEmailServer } from 'features/onboard/onboardAction';
import { selectUser, selectUserLoginId } from 'features/auth/authSlice';
import { UserStatus } from 'hrm-common/extensions/enums/personel';
import employeeApi from 'api/employeeApi';
import SelectEmailServerToSendOffboard from 'features/offboard/components/SelectEmailServerToSendOffboard';
import { useDispatch } from 'react-redux';
import { sendOnboardAndFillDataLink } from 'features/employee/employeeAction';
import { selectOnboard } from 'features/onboard/onboardSlice';

interface FooterButtonsProps {
  profileStatus: string;
  permissions: Array<string>;
  handleSave: () => void;
  handleResetAll: () => void;
  handleSaveAndSendRequestApprove: () => void | null;
  handleSaveAndApproveProfile: () => void | null;
  handleSendRequestApprove: () => void | null;
  handleApproveProfile: () => void | null;
  handleRejectProfile: () => void | null;
  handleDeleteProfile: () => void | null;
  employeeId: string;
}
const FooterButtons = (props: FooterButtonsProps) => {
  const {
    profileStatus,
    permissions,
    handleSave,
    handleResetAll,
    handleSaveAndSendRequestApprove,
    handleSaveAndApproveProfile,
    handleSendRequestApprove,
    handleApproveProfile,
    handleRejectProfile,
    handleDeleteProfile,
    employeeId,
  } = props;

  const { t } = useTranslation(['action']);
  const dispatch = useDispatch();
  const userLoginId = useAppSelector(selectUserLoginId);
  const currentUser = useAppSelector(selectUser);
  const onboardState = useAppSelector(selectOnboard);
  const [showSelectServerModal, setShowSelectServerModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingRemindEmail, setSendingRemindEmail] = useState(false);
  const [hasOnboardEmailSent, setHasOnboardEmailSent] = useState(false);

  // Kiểm tra xem email onboard đã được gửi chưa khi component mount
  useEffect(() => {
    // Kiểm tra trong localStorage xem đã gửi email onboard cho employee này chưa
    const sentEmailKey = `welcome_onboard_sent_${employeeId}`;
    const emailSent = localStorage.getItem(sentEmailKey) === 'true';
    setHasOnboardEmailSent(emailSent);
  }, [employeeId]);

  // Lấy danh sách email server khi component mount
  useEffect(() => {
    dispatch(getListEmailServer({}));
  }, [dispatch]);

  const handleSendOnboardLink = () => {
    setShowSelectServerModal(true);
  };

  const handleRemindEmailOnboard = async () => {
    // Sử dụng email server mặc định với ID = 1 (vì trên production chỉ có 1 email server duy nhất)
    const defaultEmailServerId = 1;

    // Gửi email onboard trực tiếp qua API chính thống
    setSendingRemindEmail(true);
    try {
      await dispatch(
        sendOnboardAndFillDataLink({ ids: [employeeId], configId: defaultEmailServerId }),
      );
      message.success('Gửi email onboard thành công!');
    } catch (err: any) {
      console.error('Error sending remind email:', err);
      message.error('Gửi email onboard thất bại!');
    } finally {
      setSendingRemindEmail(false);
    }
  };

  const handleSendServerSelected = async (serverId: string) => {
    setSendingEmail(true);
    try {
      const result = await dispatch(
        sendOnboardAndFillDataLink({ ids: [employeeId], configId: Number(serverId) }),
      );
      message.success('Gửi email onboard thành công!');
      setShowSelectServerModal(false);

      // Lưu trạng thái đã gửi email onboard vào localStorage
      const sentEmailKey = `welcome_onboard_sent_${employeeId}`;
      localStorage.setItem(sentEmailKey, 'true');
      setHasOnboardEmailSent(true);
    } catch (err: any) {
      message.error('Gửi email onboard thất bại!');
    } finally {
      setSendingEmail(false);
    }
  };

  // Đã xóa hàm handleRemindServerSelected vì không cần thiết nữa

  return (
    <>
      {profileStatus !== PROFILE_INACTIVE && (
        <CustomButton aType="primary" key="save-button" onClick={handleSave}>
          {t('action:save')}
        </CustomButton>
      )}
      {/* Modal add */}
      {profileStatus === 'IsAddEmployee' &&
        permissions.includes(PERMISSION_PERSONNEL_RECORD_REQUEST_APPROVAL) && (
          <CustomButton
            aType="primary"
            key="send-request-button"
            onClick={handleSaveAndSendRequestApprove}
          >
            {t('action:send_request')}
          </CustomButton>
        )}
      {profileStatus === 'IsAddEmployee' &&
        permissions.includes(PERMISSION_PERSONNEL_RECORD_APPROVAL) && (
          <CustomButton aType="primary" key="approve-button" onClick={handleSaveAndApproveProfile}>
            {t('action:approve')}
          </CustomButton>
        )}
      {/* ------------ */}
      {/* Modal update */}
      {(profileStatus === PROFILE_DRAFT || profileStatus === PROFILE_DENIED) &&
        permissions.includes(PERMISSION_PERSONNEL_RECORD_REQUEST_APPROVAL) && (
          <CustomButton
            aType="primary"
            key="send-request-button"
            onClick={handleSendRequestApprove}
          >
            {t('action:send_request')}
          </CustomButton>
        )}
      {(profileStatus === PROFILE_DRAFT ||
        profileStatus === PROFILE_PENDING ||
        profileStatus === PROFILE_DENIED) &&
        permissions.includes(PERMISSION_PERSONNEL_RECORD_APPROVAL) && (
          <CustomButton aType="primary" key="approve-button" onClick={handleApproveProfile}>
            {t('action:approve')}
          </CustomButton>
        )}
      {(profileStatus === PROFILE_DRAFT || profileStatus === PROFILE_PENDING) &&
        permissions.includes(PERMISSION_PERSONNEL_RECORD_APPROVAL) && (
          <CustomButton onClick={handleRejectProfile} aType="primary" key="reject-button">
            {t('action:reject')}
          </CustomButton>
        )}
      {profileStatus === PROFILE_DRAFT &&
        (permissions.includes(PERMISSION_PERSONNEL_RECORD_APPROVAL) ||
          permissions.includes(PERMISSION_PERSONNEL_RECORD_REQUEST_APPROVAL)) && (
          <CustomButton onClick={handleDeleteProfile} aType="primary" key="delete-button">
            {t('action:delete')}
          </CustomButton>
        )}
      {/* ------------ */}
      <CustomButton aType="outline" key="reset-button" onClick={handleResetAll}>
        {t('action:reset_all')}
      </CustomButton>

      {/* Button gửi email onboard nếu đã duyệt và đã gửi email onboard trước đó */}
      {profileStatus === UserStatus.APPROVED && hasOnboardEmailSent && (
        <>
          <CustomButton
            aType="primary"
            key="remind-email-onboard"
            onClick={handleRemindEmailOnboard}
            loading={sendingRemindEmail}
            disabled={sendingRemindEmail}
          >
            {sendingRemindEmail ? 'Sending...' : 'Remind Email Onboard'}
          </CustomButton>
        </>
      )}

      {/* Button gửi email onboard & điền thông tin nếu đã duyệt và chưa gửi email onboard */}
      {profileStatus === UserStatus.APPROVED && !hasOnboardEmailSent && (
        <>
          <CustomButton aType="primary" key="onboard-link" onClick={handleSendOnboardLink}>
            Gửi email onboard & điền thông tin
          </CustomButton>
          <Modal
            open={showSelectServerModal}
            onCancel={() => setShowSelectServerModal(false)}
            footer={null}
            title="Chọn email server để gửi onboard"
          >
            <SelectEmailServerToSendOffboard
              onSend={handleSendServerSelected}
              loading={sendingEmail}
            />
          </Modal>
        </>
      )}
    </>
  );
};

export default FooterButtons;
